import { useState, useCallback, useRef } from 'react';
import Header from './components/Header';
import UploadZone from './components/UploadZone';
import UploadOptions from './components/UploadOptions';
import TaskList from './components/TaskList';
import ResultViewer from './components/ResultViewer';
import ToastContainer from './components/Toast';
import { uploadFile } from './services/api';

let toastIdCounter = 0;

export default function App() {
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [uploadOptions, setUploadOptions] = useState({ format: 'markdown', legal: false, lang: 'fr' });
  const [toasts, setToasts] = useState([]);
  const notifiedRef = useRef(new Set());

  const addToast = useCallback((toast) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, ...toast }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const [uploadProgress, setUploadProgress] = useState(null);

  const handleUpload = useCallback(
    async (file) => {
      try {
        setUploadProgress(0);
        const data = await uploadFile(file, uploadOptions, (percent) => {
          setUploadProgress(percent);
        });
        setUploadProgress(null);
        const newTask = {
          taskId: data.taskId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.name.split('.').pop().toLowerCase(),
          status: data.status,
          createdAt: new Date().toISOString(),
          options: { ...uploadOptions },
        };
        setTasks((prev) => [newTask, ...prev]);
        setSelectedTaskId(data.taskId);
        addToast({
          type: 'success',
          title: 'Fichier soumis',
          message: `${file.name} est en file d'attente pour traitement.`,
        });
      } catch (err) {
        setUploadProgress(null);
        const statusCode = err.statusCode;
        const code = err.code;
        let title = 'Erreur d\'upload';
        let type = 'error';

        if (statusCode === 415) {
          title = 'Format non supporte';
          type = 'warning';
        } else if (statusCode === 413) {
          title = 'Fichier trop volumineux';
          type = 'warning';
        } else if (statusCode === 429) {
          title = 'Limite de requetes atteinte';
          type = 'warning';
        } else if (statusCode === 401) {
          title = 'Cle API invalide';
        } else if (statusCode === 503) {
          title = 'Service indisponible';
        }

        addToast({ type, title, message: err.message, code });
      }
    },
    [uploadOptions, addToast],
  );

  const handleReject = useCallback(
    (message) => {
      addToast({ type: 'warning', title: 'Fichier refuse', message });
    },
    [addToast],
  );

  const handleTaskUpdate = useCallback(
    (taskId, updatedData) => {
      setTasks((prev) => {
        const existing = prev.find((t) => t.taskId === taskId);

        // Only notify when status actually transitions to completed/failed
        if (
          updatedData.status === 'completed' &&
          existing?.status !== 'completed' &&
          !notifiedRef.current.has(taskId)
        ) {
          notifiedRef.current.add(taskId);
          addToast({
            type: 'success',
            title: 'Traitement termine',
            message: `${existing?.fileName || 'Document'} a ete converti avec succes.`,
          });
        }

        if (
          updatedData.status === 'failed' &&
          existing?.status !== 'failed' &&
          !notifiedRef.current.has(taskId)
        ) {
          notifiedRef.current.add(taskId);
          addToast({
            type: 'error',
            title: 'Echec du traitement',
            message: updatedData.error || `Le traitement de ${existing?.fileName || 'document'} a echoue.`,
          });
        }

        return prev.map((t) => (t.taskId === taskId ? { ...t, ...updatedData } : t));
      });
    },
    [addToast],
  );

  const handleTaskDelete = useCallback(
    (taskId) => {
      setTasks((prev) => prev.filter((t) => t.taskId !== taskId));
      notifiedRef.current.delete(taskId);
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
      }
    },
    [selectedTaskId],
  );

  const selectedTask = tasks.find((t) => t.taskId === selectedTaskId) || null;

  return (
    <div className="app-layout">
      <Header />
      <div className="app-main">
        <div className="panel-left">
          <UploadZone onUpload={handleUpload} onReject={handleReject} />
          {uploadProgress !== null && (
            <div className="upload-progress">
              <div className="upload-progress-label">Envoi en cours... {uploadProgress}%</div>
              <div className="progress-bar-track" style={{ height: 6 }}>
                <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}
          <UploadOptions options={uploadOptions} onChange={setUploadOptions} />
          <div className="panel-body">
            <TaskList
              tasks={tasks}
              selectedTaskId={selectedTaskId}
              onSelect={setSelectedTaskId}
              onTaskUpdate={handleTaskUpdate}
              onDelete={handleTaskDelete}
            />
          </div>
        </div>
        <div className="panel-right">
          <ResultViewer task={selectedTask} onTaskUpdate={handleTaskUpdate} />
        </div>
      </div>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
